// =============================================================================
// src/features/reputation/reputation-charts.ts
//
// Construtores de charts SVG/HTML feitos a mao (gauge, serie, donut, canais,
// mencoes negativas, ranking). Porte FIEL de assets/reputation.js:
//   buildArea 46-61, buildPolyline 63-73, drawGauge 76-89, drawDonut 190-217,
//   drawChannels 228-244, drawNeg 252-263, drawRank 266-286.
//
// Geometria/coordenadas/atributos COPIADOS byte-a-byte (paridade visual). Cada
// funcao tem 4-20 linhas; nomes greppaveis (build*/draw* com prefixo de feature).
// SRP: so producao de markup/atributos SVG a partir de dados. Sem interacao.
// =============================================================================

import { byId, qsa } from '../../app/dom';
import type { ReputationAreaMeta, ReputationChannelTotals } from './reputation-types';
import {
  REPUTATION_CHANNELS,
  REPUTATION_CHAN_LEGEND,
  REPUTATION_POLS,
  REPUTATION_SEGMENT_AVG,
  REPUTATION_SOV,
  formatReputationNumber,
  reputationBand,
} from './reputation-data';

/** Namespace SVG usado nos createElementNS — assets/reputation.js 194. */
const SVG_NS = 'http://www.w3.org/2000/svg';

/** Resolve um elemento SVG por id ou null (charts toleram ausencia, igual ao legado). */
function svgById(id: string): SVGElement | null {
  return document.getElementById(id) as SVGElement | null;
}

/**
 * Calcula coordenadas e desenha linha+area de uma serie. Porte FIEL de `buildArea`
 * (reputation.js 46-61); devolve meta para a interacao da serie ou null se ausente.
 */
export function buildReputationAreaPath(
  lineId: string,
  areaId: string,
  data: readonly number[],
  w: number,
  h: number,
  padX: number,
  padTop: number,
  padBottom: number,
  yMin: number,
  yMax: number,
): ReputationAreaMeta | null {
  const line = svgById(lineId);
  const area = svgById(areaId);
  if (!line || !area) return null;
  const coords = computeAreaCoords(data, w, h, padX, padTop, padBottom, yMin, yMax);
  const step = (w - padX * 2) / (data.length - 1);
  const dPath = coords.map((c, i) => `${i ? 'L' : 'M'}${c[0].toFixed(1)} ${c[1].toFixed(1)}`).join(' ');
  line.setAttribute('d', dPath);
  area.setAttribute('d', closeAreaPath(dPath, coords, h, padBottom));
  return { step, padX, w, h, coords, yMin, yMax };
}

/** Mapeia cada valor da serie para um par [x,y] clampado — extrai o map de buildArea. */
function computeAreaCoords(
  data: readonly number[],
  w: number,
  h: number,
  padX: number,
  padTop: number,
  padBottom: number,
  yMin: number,
  yMax: number,
): (readonly [number, number])[] {
  const range = (yMax - yMin) || 1;
  const step = (w - padX * 2) / (data.length - 1);
  return data.map((v, i) => {
    const x = padX + i * step;
    const yRaw = (h - padBottom) - ((v - yMin) / range) * (h - padTop - padBottom);
    return [x, Math.max(padTop, Math.min(h - padBottom, yRaw))] as const;
  });
}

/** Fecha o path da area ate a base — porte da linha 59 de reputation.js. */
function closeAreaPath(
  dPath: string,
  coords: readonly (readonly [number, number])[],
  h: number,
  padBottom: number,
): string {
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (!first || !last) return dPath;
  const baseY = h - padBottom;
  return `${dPath} L${last[0].toFixed(1)} ${baseY} L${first[0].toFixed(1)} ${baseY} Z`;
}

/** Desenha uma polyline simples (mencoes negativas) — porte FIEL de `buildPolyline`. */
export function buildReputationPolyline(
  id: string,
  data: readonly number[],
  w: number,
  h: number,
  padX: number,
  padTop: number,
  padBottom: number,
  yMax: number,
): void {
  const el = svgById(id);
  if (!el) return;
  const step = (w - padX * 2) / (data.length - 1);
  const pts = data.map((v, i) => {
    const x = padX + i * step;
    const y = (h - padBottom) - (v / yMax) * (h - padTop - padBottom);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  el.setAttribute('points', pts.join(' '));
}

/** Gira a agulha do gauge para 53,15 e anota o titulo das dimensoes (drawGauge 76-89). */
export function drawReputationGauge(takeover: HTMLElement): void {
  const needle = svgById('repGaugeNeedle');
  if (!needle) return;
  const deg = (53.15 - 50) * 1.8; // -90°(0) … 0°(50) … +90°(100)
  (needle as unknown as HTMLElement).style.transform = `rotate(${deg.toFixed(2)}deg)`;
  qsa<HTMLElement>('.rep-dim-val[data-band]', takeover).forEach((el) => {
    /* v8 ignore next -- seletor [data-band] garante dataset.band presente; ?? '0' defensivo p/ noUncheckedIndexedAccess */
    el.title = reputationBand(parseFloat(el.dataset['band'] ?? '0')).name;
  });
}

/** Monta os arcos do donut + a legenda de Share of Voice (drawDonut 190-217). */
export function drawReputationDonut(): void {
  const svg = svgById('repDonut');
  if (!svg || svg.childNodes.length) return;
  const cx = 90, cy = 90, r = 66, sw = 26;
  const circumference = 2 * Math.PI * r;
  let off = 0;
  REPUTATION_SOV.forEach((slice) => {
    const len = (slice.pct / 100) * circumference;
    svg.appendChild(buildDonutArc(slice.color, cx, cy, r, sw, len, circumference, off));
    off += len;
  });
  renderSovLegend();
}

/** Cria um <circle> de arco do donut (extrai o corpo do forEach de drawDonut). */
function buildDonutArc(
  color: string, cx: number, cy: number, r: number, sw: number,
  len: number, circumference: number, off: number,
): SVGCircleElement {
  const c = document.createElementNS(SVG_NS, 'circle');
  c.setAttribute('cx', String(cx));
  c.setAttribute('cy', String(cy));
  c.setAttribute('r', String(r));
  c.setAttribute('fill', 'none');
  c.setAttribute('stroke', color);
  c.setAttribute('stroke-width', String(sw));
  c.setAttribute('stroke-dasharray', `${len.toFixed(2)} ${(circumference - len).toFixed(2)}`);
  c.setAttribute('stroke-dashoffset', (-off).toFixed(2));
  return c;
}

/** Preenche a legenda do donut (segunda metade de drawDonut 210-216). */
function renderSovLegend(): void {
  const legend = document.getElementById('repSovLegend');
  if (!legend) return;
  legend.innerHTML = REPUTATION_SOV.map((s) =>
    `<li><span class="sw" style="background:${s.color}"></span>` +
    `<span class="nm">${s.nm} <small>${s.sub}</small></span>` +
    `<span class="pc">${s.pct}%</span></li>`,
  ).join('');
}

/** Monta as barras por canal + legenda (drawChannels 228-244). */
export function drawReputationChannels(): void {
  const host = document.getElementById('repChannels');
  if (!host || host.childNodes.length) return;
  const rows = REPUTATION_CHANNELS.map((ch) => {
    const segs = ch.segs.map((s) => `<span class="rep-chan-seg" style="width:${s[1]}%;background:${s[0]}"></span>`).join('');
    return '<div class="rep-chan">' +
      `<div class="rep-chan-top"><span class="rep-chan-name"><i class="ti ${ch.icon} rep-ico sm" aria-hidden="true"></i>${ch.name}</span>` +
      `<span class="rep-chan-total">${ch.total} menções</span></div>` +
      `<div class="rep-chan-bar">${segs}</div></div>`;
  }).join('');
  const legend = '<div class="rep-chan-legend">' +
    REPUTATION_CHAN_LEGEND.map((l) => `<span><i style="background:${l[0]}"></i>${l[1]}</span>`).join('') +
    '</div>';
  host.innerHTML = rows + legend;
}

/** Desenha as 3 series negativas + a legenda (drawNeg 252-263). */
export function drawReputationNeg(neg: ReputationChannelTotals): void {
  buildReputationPolyline('repNeg1', neg.helena, 600, 150, 6, 12, 14, 1000);
  buildReputationPolyline('repNeg2', neg.ricardo, 600, 150, 6, 12, 14, 1000);
  buildReputationPolyline('repNeg3', neg.marina, 600, 150, 6, 12, 14, 1000);
  renderNegLegend();
}

/** Preenche a legenda das mencoes negativas (drawNeg 256-262). */
function renderNegLegend(): void {
  const legend = document.getElementById('repNegLegend');
  if (!legend || legend.childNodes.length) return;
  legend.innerHTML =
    '<li><span class="ln" style="border-color:#E24B4A"></span>Omar Aziz <small style="color:var(--rep-faint)">(você · pico 16/09)</small></li>' +
    '<li><span class="ln" style="border-color:#378ADD"></span>Ricardo Nogueira</li>' +
    '<li><span class="ln" style="border-color:#7F77DD"></span>Marina Castro</li>';
}

/** Renderiza o ranking ordenado por indice + linha de media (drawRank 266-286). */
export function drawReputationRank(): void {
  const host = document.getElementById('repRank');
  if (!host || host.childNodes.length) return;
  const sorted = REPUTATION_POLS.slice().sort((a, b) => b.index - a.index);
  const rows = sorted.map(buildRankRow).join('');
  const avg = `<div class="rep-rank-avg-wrap"><div class="rep-rank-avg" data-label="Média do segmento: ${REPUTATION_SEGMENT_AVG}" style="left:${REPUTATION_SEGMENT_AVG}%"></div></div>`;
  host.innerHTML = rows + avg;
}

/** Monta a linha de um politico no ranking (extrai o map de drawRank 270-282). */
function buildRankRow(p: (typeof REPUTATION_POLS)[number]): string {
  const b = reputationBand(p.index);
  const w = p.index; // % da trilha (0-100)
  const inside = w >= 18;
  const insideScore = inside ? `<span class="rep-rank-score">${formatReputationNumber(p.index)}</span>` : '';
  const outsideScore = inside ? '' : `<span class="rep-rank-score outside" style="left:calc(${w}% + 8px)">${formatReputationNumber(p.index)}</span>`;
  return '<div class="rep-rank-row">' +
    `<div class="rep-rank-id"><span class="rep-rank-party" style="background:${p.color}"></span>` +
    `<span class="rep-rank-name">${p.name} <small>${p.party}</small></span></div>` +
    `<div class="rep-rank-track"><span class="rep-rank-fill ${b.key}" style="width:${w}%">${insideScore}</span>` +
    `${outsideScore}</div>` +
    '</div>';
}

/** Re-exporta byId para os modulos irmaos que precisam do boundary unico. */
export { byId };
